<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/use-locale';
import QuizterCard from '../components/QuizterCard.vue';
import ConfirmDialog from '../components/host/ConfirmDialog.vue';
import QuizterLayout from '../layout/QuizterLayout.vue';
import type { SharedQuizter } from '@ilteducation/quizter-types';
import { useQuizterStore } from '../stores/quizterStore';

const props = defineProps<{
  start: (resourceIds: string[], activityId: string) => void | Promise<void>;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
}>();

const { xs } = useDisplay();
const { t } = useLocale();
const quizterStore = useQuizterStore();

const quizters = ref<SharedQuizter[]>([]);
const isLoading = ref(true);
const hasError = ref(false);
const startingActivityId = ref<string | null>(null);
const quizterToUnsubscribe = ref<SharedQuizter | null>(null);
const unsubscribingShareId = ref<string | null>(null);
const unsubscribeError = ref(false);

const loadQuizters = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    quizters.value = await quizterStore.fetchMySubscribedQuizters();
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadQuizters);

const handleStart = async (quizter: SharedQuizter) => {
  if (quizter.resourceIds.length === 0 || startingActivityId.value || unsubscribingShareId.value) {
    return;
  }
  startingActivityId.value = quizter.activityId;
  try {
    await props.start(quizter.resourceIds, quizter.activityId);
  } finally {
    startingActivityId.value = null;
  }
};

const openUnsubscribeDialog = (quizter: SharedQuizter) => {
  if (startingActivityId.value || unsubscribingShareId.value) {
    return;
  }
  unsubscribeError.value = false;
  quizterToUnsubscribe.value = quizter;
};

const closeUnsubscribeDialog = () => {
  if (unsubscribingShareId.value) {
    return;
  }
  quizterToUnsubscribe.value = null;
};

const confirmUnsubscribe = async () => {
  const quizter = quizterToUnsubscribe.value;
  if (!quizter) {
    return;
  }
  unsubscribingShareId.value = quizter.shareId;
  unsubscribeError.value = false;
  try {
    await quizterStore.unsubscribeSharedQuizter(quizter.shareId);
    quizters.value = quizters.value.filter((q) => q.shareId !== quizter.shareId);
    quizterToUnsubscribe.value = null;
  } catch {
    unsubscribeError.value = true;
  } finally {
    unsubscribingShareId.value = null;
  }
};
</script>

<template>
  <QuizterLayout :show-header="false" :show-back-button="true" back-button-pos="topLeft" @back="emit('back')">
    <div class="d-flex flex-column align-center justify-center ga-12">
      <h1 class="chalkduster-font text-center text-h1">{{ t('shared_with_me.title') }}</h1>

      <!-- Loading -->
      <div v-if="isLoading" class="loading d-flex flex-column align-center ga-4">
        <v-progress-circular indeterminate size="48"></v-progress-circular>
      </div>

      <!-- Error -->
      <div v-else-if="hasError" class="empty-state d-flex flex-column align-center ga-4">
        <v-icon icon="fa:fawsb fa-circle-exclamation" class="opacity-50" size="64"></v-icon>
        <p class="chalkboard-font text-center text-h6 ma-0 opacity-70">
          {{ t('shared_with_me.error') }}
        </p>
        <v-btn variant="outlined" class="text-none chalkduster-font" @click="loadQuizters">
          {{ t('shared_with_me.retry') }}
        </v-btn>
      </div>

      <!-- Empty -->
      <div v-else-if="quizters.length === 0" class="empty-state d-flex flex-column align-center ga-4">
        <v-icon icon="fa:fawsb fa-heart" class="opacity-50" size="64"></v-icon>
        <p class="chalkboard-font text-center text-h6 ma-0 opacity-70">
          {{ t('shared_with_me.empty') }}
        </p>
      </div>

      <!-- List -->
      <div v-else class="quizter-list d-flex flex-column ga-4 overflow-y-auto">
        <QuizterCard
          v-for="quizter in quizters"
          :key="quizter.shareId"
          :resource-ids="quizter.resourceIds"
          :name="quizter.name || t('shared_with_me.untitled')"
          :description="`${t('shared_with_me.questions', { count: quizter.questionsLength })} ${
            quizter.description ? `· ${quizter.description}` : ''
          }`"
          :disabled="startingActivityId === quizter.activityId || unsubscribingShareId === quizter.shareId"
          :is-starting="startingActivityId === quizter.activityId"
          @start="handleStart(quizter)"
        >
          <template #actions>
            <div class="quizter-actions d-flex ga-2">
              <v-btn
                icon
                size="small"
                variant="text"
                :disabled="startingActivityId === quizter.activityId"
                :aria-label="t('shared_with_me.unsubscribe')"
                data-testid="shared-quizter-unsubscribe"
                @click.stop="openUnsubscribeDialog(quizter)"
              >
                <v-icon icon="fa:fawsb fa-xmark" size="18"></v-icon>
                <v-tooltip activator="parent" location="top" aria-hidden="true" :disabled="xs">
                  <span>{{ t('shared_with_me.unsubscribe') }}</span>
                </v-tooltip>
              </v-btn>
            </div>
          </template>
        </QuizterCard>
      </div>
    </div>

    <ConfirmDialog
      :model-value="!!quizterToUnsubscribe"
      :title="t('shared_with_me.unsubscribe_confirm_title')"
      :message="
        t('shared_with_me.unsubscribe_confirm_message', {
          name: quizterToUnsubscribe?.name || t('shared_with_me.untitled'),
        })
      "
      :confirm-label="t('shared_with_me.unsubscribe_confirm_button')"
      :cancel-label="t('shared_with_me.unsubscribe_cancel_button')"
      :error-message="t('shared_with_me.unsubscribe_error')"
      :error="unsubscribeError"
      :loading="!!unsubscribingShareId"
      confirm-color="error"
      @update:model-value="closeUnsubscribeDialog"
      @confirm="confirmUnsubscribe"
    />
  </QuizterLayout>
</template>

<style scoped lang="scss">
.empty-state {
  min-height: 200px;
  max-width: 500px;
}

.loading {
  min-height: 200px;
}

.quizter-list {
  width: min(600px, calc(100vw - 3rem));
  // Keep the list inside its own scroll area so the whole page doesn't scroll.
  max-height: 60vh;
  // Breathing room so the hover scale and icon outline aren't clipped by the scroll area.
  padding: 0.5rem;

  // Custom scrollbar styling to match the Quizter theme (same as VideoList / AddVideoDialog).
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(var(--v-theme-on-surface), 0.3);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.5);
  }
}

// Action buttons in the top-right corner of the card (matches MyQuiztersStartPage).
.quizter-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}
</style>
